<template>
  <div>
    <md-dialog
      :md-active.sync="visibleCurrentForm"
      :md-close-on-esc="false"
      :md-click-outside-to-close="false"
    >
      <md-dialog-title>Insurance Quote</md-dialog-title>

      <md-dialog-content>
        <div v-if="parsedMetadata">
          <p><span style="font-weight:bold;">Quote UUID:</span> <span>{{ parsedMetadata.quote_uuid }}</span></p>
          <br/>
          <p style="font-weight:bold;">Landing Page URL:</p>
          <div class="url-container">
            <input type="text" :value="parsedMetadata.landing_page_url" readonly ref="landingPageInput" style="width: 500px;">
            <md-button class="md-primary" @click="copyToClipboard" style="width: 40px;">Copy URL</md-button>
          </div>
        </div>
        <p v-else>No insurance metadata available.</p>
      </md-dialog-content>

      <md-dialog-actions>
        <loading-icon-text-button
          v-if="parsedMetadata"
          icon="send"
          class="md-primary"
          @click="sendSMS"
          :loading="loading">
          Send SMS
        </loading-icon-text-button>
        <loading-icon-text-button
          v-if="parsedMetadata"
          icon="send"
          class="md-primary"
          @click="sendEmail"
          :loading="loading">
          Send Email
        </loading-icon-text-button>
        <md-button class="md-primary" @click="onSubmit">Close</md-button>
      </md-dialog-actions>
    </md-dialog>
    <md-dialog-alert
      :md-active.sync="error_alert.show"
      :md-content="error_alert.message"
      md-confirm-text="Close"
    />
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import ValidatedInputs from "@/components/ValidatedInput";
import AlertDialog from "@/components/Dialogs/AlertDialog";
import ConfirmDialog from "@/components/Dialogs/ConfirmDialog";
import LoadingIconTextButton from "@/components/LoadingIconTextButton";
import axios from "axios";

export default {
  components:{
    LoadingIconTextButton,
    ...ValidatedInputs,
    ConfirmDialog,
    AlertDialog
  },
  data() {
    return {
      visibleCurrentForm: false,
      error_alert:{
        show: false,
        message:''
      },
      loading: false
    };
  },
  props: {
    visible: {
      default: false,
      type: Boolean,
    },
    quoteMetadata: {
      type: Object
    }
  },
  computed: {
    ...mapGetters([
      'application',
      "options",
    ]),
    parsedMetadata() {
      try {
        const metadata = this.application?.loan?.quotes?.insurance?.metadata || this.quoteMetadata;

        if (!metadata) return null; // If no metadata, return null

        // ✅ If metadata is an object, return it directly
        if (typeof metadata === "object") return metadata;

        // ✅ If metadata is a JSON string, ensure proper parsing
        return JSON.parse(metadata.startsWith("{") ? metadata : JSON.parse(metadata));

      } catch (error) {
        console.error("Error parsing metadata:", error);
        return null;
      }
    }
  },
  methods: {
    onSubmit() {
      this.visibleCurrentForm = false;
      this.$emit("close");
    },
    async sendSMS() {
      await this.sendGenerateQuoteToCustomer('sms');
      this.visibleCurrentForm = false;
      this.$emit("close");
    },
    async sendEmail() {
      await this.sendGenerateQuoteToCustomer('email');
      this.visibleCurrentForm = false;
      this.$emit("close");
    },
    async sendGenerateQuoteToCustomer(communication_type) {
      if (!this.parsedMetadata?.landing_page_url) {
        this.showErrorDialog("Invalid quote metadata. Cannot proceed.", true);
        return;
      }

      const res = await this.showNotification(
        "Confirm",
        `Do you confirm to send the insurance quote to customer via ${communication_type}?`,
        "PROCEED",
        "CANCEL"
      );

      if (!res) {
        return false;
      }

      this.loading = true;
      try {
        await axios.post("/api/v1/application/sendGenerateQuoteToCustomer", {
          'loan_application_id': this.application?.loan?.id,
          'applicant_id': this.application?.applicants?.[0]?.id,
          'quote_url': this.parsedMetadata?.landing_page_url,
          'communication_type': communication_type
        });

        this.showNotification(
          "",
          `Insurance quote link sent successfully via ${communication_type}.`,
          "OK"
        );
      } catch (err) {
        this.showNotification(
          "",
          "Failed to send the insurance quote. Please try again.",
          "OK"
        );
        console.error("Error sending insurance quote:", err);
      } finally {
        this.loading = false;
      }
    }
    ,
    onCancel() {
      this.visibleCurrentForm = false;
      this.$emit("close");
    },
    showErrorDialog(message, flag){
      this.error_alert=
      {
        message: message,
          show: flag
      };
    },
    showNotification(title, message, confirmText, cancelText = null) {
      return this.$modal.show(ConfirmDialog, {
        title,
        message,
        confirmText,
        cancelText
      })
    },
    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.parsedMetadata.landing_page_url);
        this.showNotification("Success", "Copied to clipboard!", "OK");
      } catch (err) {
        console.error("Failed to copy:", err);
        this.showNotification("Error", "Could not copy to clipboard.", "OK");
      }
    }
  },
  created() {
  },
  watch: {
    visible (value) {
      this.visibleCurrentForm = value;
    },
  }
};
</script>

<style scoped>
/* Override default md-dialog width for better layout */
.insuranceQuoteForm {
  display: grid;
  grid-template-columns: 1fr 1fr;
}
.url-container {
  display: flex;
  flex-direction: column;
}

:v-deep(.md-dialog-container) {
  width: 80%;
}
</style>
